import { CircleDashed } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { WorkflowInspector } from '~/features/workflow-inspection/components/workflow-inspector';
import { useSensitiveMask } from '~/features/workflow-inspection/hooks/use-sensitive-mask';

import type { DeliveryRunDetail } from '../../types/delivery-runs.types';

import { RetryChain } from './retry-chain';

interface Props {
  /** Unmasked run — masked here for the fallbacks; lineage ids stay raw. */
  run: DeliveryRunDetail;
  /** Open another run of the retry chain (host decides: switch in place or navigate). */
  onSelectRun: (runId: string) => void;
  /** Show the inspector's own "Reveal sensitive data" switch (false when the host renders one). */
  showRevealToggle?: boolean;
  /** `data-testid` for the waiting note / retry chain (`-waiting`, `-retry-chain`). */
  testId?: string;
}

/**
 * The Temporal half of a delivery run, shared by the wizard's run step, the
 * deliveries console's Workflow tab and the Compare Delivery page: the
 * generic `WorkflowInspector` for the run's own workflow, with the run's
 * saved result/failure (masked) as the fallback once Temporal history has
 * expired, and the retry chain in the Relationships tab. Shows a waiting
 * note until the run has started a workflow.
 */
export function DeliveryRunWorkflowPanel({
  run,
  onSelectRun,
  showRevealToggle = true,
  testId = 'delivery-run-workflow-panel',
}: Props) {
  const { t } = useTranslation('deliveries');
  const { mask } = useSensitiveMask([run.id, run.workflowId]);

  if (!run.workflowId) {
    return (
      <p
        data-testid={`${testId}-waiting`}
        className="text-muted-foreground flex items-center gap-2 rounded-lg border p-4 text-sm"
      >
        <CircleDashed
          aria-hidden="true"
          className="size-4 animate-spin motion-reduce:animate-none"
        />
        {t('inspector.waitingForWorkflow')}
      </p>
    );
  }

  return (
    <WorkflowInspector
      key={run.workflowId}
      workflowId={run.workflowId}
      fallbackResult={run.result ? mask(run.result) : undefined}
      fallbackFailure={run.failure ? mask(run.failure) : null}
      showRevealToggle={showRevealToggle}
      // Lineage ids are this tenant's own run ids (like the header's run id) and
      // must stay usable for switching runs, so the chain gets the raw run.
      relationshipsExtra={
        <RetryChain run={run} onSelect={onSelectRun} testId={`${testId}-retry-chain`} />
      }
    />
  );
}
